import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { DataService, ViewService } from '@xanadu/service';
import { DynamicComponent } from '@xanadu/core';
import { ActivatedRoute } from '@angular/router';
import { CommonSignal } from '@xanadu/signal';
import { ViewQuery } from '@xanadu/model';
import { CacheAggregator } from '../../cache/cache.aggregator';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-view',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewComponent extends DynamicComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly cacheAggregator = inject(CacheAggregator);
    private readonly commonSignal = inject(CommonSignal);
    private readonly dataService = inject(DataService);
    private readonly viewService = inject(ViewService);

    readonly view = signal<any>(undefined);
    readonly url = toSignal(this.route.url, {
        initialValue: []
    });

    constructor(
    ) {
        super();

        effect(() => {
            const segments = this.url();

            if (segments) {
                const params: string[] = segments.map(x => x.path) ?? [];
                let name = params.shift();

                if (name) {
                    const viewQuery = new ViewQuery(name, params);
                    this.commonSignal.view.set(viewQuery);
                }
            }
        });

        effect(() => {
            const ready = this.cacheAggregator.ready();

            if (ready) {
                void this.loadRoot();
            }
        });
    }

    private async loadRoot(): Promise<void> {
        await firstValueFrom(this.dataService.getRoot())
            .then(async root => {
                if (root) {
                    const tabs: Promise<any>[] = [];
                    if (root.content?._id) {
                        tabs.push(this.viewService.tab('content', root.content));
                        tabs.push(this.viewService.tab('batch', root.content));
                    }
                    if (root.aspect?._id) {
                        tabs.push(this.viewService.tab('aspect', root.aspect));
                    }
                    if (root.type?._id) {
                        tabs.push(this.viewService.tab('type', root.type));
                    }
                    if (root.import.branches?.length) {
                        tabs.push(this.viewService.tab('import', root.import));
                    }
                    if (root.user?._id && root.role?._id) {
                        tabs.push(this.viewService.tab('user', root.user));
                        tabs.push(this.viewService.tab('role', root.role));
                    }
                    if (root.client?._id) {
                        tabs.push(this.viewService.tab('client', root.client));
                    }

                    const tabsResult = await Promise.all(tabs);
                    const view = { component: 'tab', tabs: tabsResult };

                    this.view.set(view);
                }
            });
    }
}
